import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Star, Store } from 'lucide-react';

interface ReviewItem {
  id: number;
  orderNum: string;
  rating: number;
  review: string;
  phone: string;
  createdAt: number;
}

interface MerchantWithReviews {
  code: string;
  name: string;
  rating: number | null;
  reviewCount: number;
  latestReviews: ReviewItem[];
}

const AdminReviews: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const [merchants, setMerchants] = useState<MerchantWithReviews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchReviews();
  }, [token, navigate]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/merchants');
      if (!res.ok) throw new Error('無法載入商家列表');
      const merchantData = await res.json();
      const merchantList = Array.isArray(merchantData) ? merchantData : (merchantData.data || []);

      const withReviews = await Promise.all(
        merchantList.map(async (m: any) => {
          const res2 = await apiFetch(`/api/public/admin/merchants/${encodeURIComponent(m.code)}/reviews`);
          const reviews = res2.ok ? await res2.json() : [];
          const avg = reviews.length > 0
            ? Math.round((reviews.reduce((s: number, r: ReviewItem) => s + r.rating, 0) / reviews.length) * 10) / 10
            : null;
          return {
            code: m.code,
            name: m.name || m.code,
            rating: avg,
            reviewCount: reviews.length,
            latestReviews: reviews.slice(0, 5),
          };
        })
      );

      setMerchants(withReviews.filter(m => m.reviewCount > 0));
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star
          key={s}
          size={16}
          className={s <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
        />
      ))}
    </div>
  );

  return (
    <AdminLayout currentPage="reviews" onLogout={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin'); }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Star className="w-6 h-6 text-amber-500" />
          客戶評價管理
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">查看各鄰里商家的客戶評分與留言</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-20 text-gray-500">載入中...</div>
      ) : merchants.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500">
          暫無客戶評價
        </div>
      ) : (
        <div className="space-y-6">
          {merchants.map(m => (
            <div key={m.code} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store className="w-5 h-5 text-orange-600" />
                  <div>
                    <h2 className="font-bold text-gray-800 dark:text-white">{m.name}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">GS Code: {m.code}</p>
                  </div>
                </div>
                <div className="text-right">
                  {m.rating !== null ? (
                    <>
                      <div className="flex items-center gap-2 justify-end">
                        {renderStars(Math.round(m.rating))}
                        <span className="font-bold text-lg text-gray-800 dark:text-white">{m.rating}</span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{m.reviewCount} 個評價</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">暫無評分</p>
                  )}
                </div>
              </div>

              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {m.latestReviews.map(r => (
                  <div key={r.id} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {renderStars(r.rating)}
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">訂單 {r.orderNum}</span>
                        </div>
                        {r.review ? (
                          <p className="text-gray-600 dark:text-gray-400 text-sm">{r.review}</p>
                        ) : (
                          <p className="text-gray-400 dark:text-gray-500 text-sm italic">無留言</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-gray-400">{r.phone}</p>
                        <p className="text-xs text-gray-400">{new Date(r.createdAt * 1000).toLocaleDateString('zh-HK')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {m.reviewCount > 5 && (
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900 text-center">
                  <button
                    onClick={() => navigate(`/admin/merchants/${encodeURIComponent(m.code)}`)}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    查看全部 {m.reviewCount} 個評價 →
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReviews;
